import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Building2, Trophy, CheckCircle2 } from "lucide-react";
import YSVLogo from "@/components/YSVLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  "Southside Chicago",
  "Westside Chicago",
  "Northside Chicago",
  "South Suburbs",
  "North Suburbs",
  "West Suburbs",
  "Southwest Suburbs",
  "Northwest Indiana",
];

const LeagueOrgSignupPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    area: "",
    signup_type: "",       // "league" | "org"
    org_count: 1,
    org_names: [""],
    team_count: 1,
    team_names: [""],
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setSignupType = (t) => {
    setForm((f) => ({
      ...f,
      signup_type: t,
      org_count: t === "league" ? Math.max(1, f.org_count || 1) : 1,
      org_names: t === "league" ? (f.org_names.length ? f.org_names : [""]) : [""],
      team_count: t === "org" ? Math.max(1, f.team_count || 1) : 1,
      team_names: t === "org" ? (f.team_names.length ? f.team_names : [""]) : [""],
    }));
  };

  const setCount = (kind, raw) => {
    const n = Math.max(1, Math.min(50, parseInt(raw || "1", 10) || 1));
    setForm((f) => {
      if (kind === "org") {
        const names = [...f.org_names];
        while (names.length < n) names.push("");
        names.length = n;
        return { ...f, org_count: n, org_names: names };
      }
      const names = [...f.team_names];
      while (names.length < n) names.push("");
      names.length = n;
      return { ...f, team_count: n, team_names: names };
    });
  };

  const setNameAt = (kind, idx, v) => {
    setForm((f) => {
      const key = kind === "org" ? "org_names" : "team_names";
      const next = [...f[key]];
      next[idx] = v;
      return { ...f, [key]: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.first_name.trim() || !form.last_name.trim()) { setError("Please enter your first and last name."); return; }
    if (!form.email.trim() || !form.email.includes("@")) { setError("Please enter a valid email."); return; }
    if (!form.phone.trim()) { setError("Please enter a phone number."); return; }
    if (!form.area) { setError("Please select your area."); return; }
    if (!form.signup_type) { setError("Please choose League or Organization."); return; }

    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      area: form.area,
      signup_type: form.signup_type,
    };
    if (form.signup_type === "league") {
      const orgs = form.org_names.map((n) => n.trim()).filter(Boolean);
      if (orgs.length === 0) { setError("Please list at least one organization name."); return; }
      payload.org_count = orgs.length;
      payload.org_names = orgs;
    } else {
      const teams = form.team_names.map((n) => n.trim()).filter(Boolean);
      if (teams.length === 0) { setError("Please list at least one team name."); return; }
      payload.team_count = teams.length;
      payload.team_names = teams;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/public/league-org-signups`, payload);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Thank-you screen ─────────────────────────────────────────────
  if (submitted) {
    return (
      <div
        data-testid="league-signup-thankyou"
        style={{
          minHeight: "100vh",
          background: "radial-gradient(1000px 700px at 50% 0%, rgba(0,229,255,0.10), transparent 60%), var(--dark-bg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "80px 24px 60px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 900, width: "100%" }}>
          <div style={{ display: "inline-flex", marginBottom: 32 }}>
            <YSVLogo size="hero" />
          </div>

          <h1
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 900,
              fontSize: "clamp(72px, 18vw, 180px)",
              lineHeight: 0.95,
              letterSpacing: "-0.03em",
              color: "#fff",
              textShadow: "0 0 40px rgba(0,229,255,0.30)",
              margin: "8px 0 20px",
            }}
          >
            THANK YOU
          </h1>

          <p
            style={{
              fontSize: "clamp(20px, 3vw, 30px)",
              color: "var(--text-secondary)",
              fontWeight: 500,
              maxWidth: 640,
              margin: "0 auto 40px",
              lineHeight: 1.5,
            }}
          >
            We will be in touch.
          </p>

          <Link
            to="/landing"
            data-testid="league-signup-thankyou-home-link"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "var(--cyan)",
              fontSize: 16,
              fontWeight: 600,
              textDecoration: "none",
              padding: "14px 28px",
              border: "1px solid var(--cyan)",
              borderRadius: 999,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--cyan)";
              e.currentTarget.style.color = "#0a0a0c";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--cyan)";
            }}
          >
            <ArrowLeft size={18} /> VISIT YSV
          </Link>
        </div>
      </div>
    );
  }

  // ── Form screen ─────────────────────────────────────────────────
  return (
    <div
      data-testid="league-signup-page"
      className="page-gradient"
      style={{ padding: "96px 24px 72px", minHeight: "100vh" }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <Link to="/get-started" style={{ display: "inline-flex", textDecoration: "none" }}>
            <YSVLogo size="md" />
          </Link>
          <p
            style={{
              fontSize: 12,
              color: "var(--cyan)",
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: "uppercase",
              marginTop: 22,
            }}
          >
            Sign up a League or Organization
          </p>
          <h1
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: "clamp(28px, 4.5vw, 42px)",
              fontWeight: 800,
              color: "#fff",
              marginTop: 6,
              lineHeight: 1.15,
            }}
          >
            Bring your league or organization onto YSV
          </h1>
          <p style={{ color: "var(--text-secondary)", fontSize: 15, marginTop: 12, lineHeight: 1.6 }}>
            Tell us a bit about you and what you run. We'll reach out personally to get you set up.
          </p>
        </div>

        <div className="glass-card" style={{ padding: 28 }}>
          <Link
            to="/get-started"
            data-testid="league-signup-back"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "var(--text-secondary)",
              textDecoration: "none",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            <ArrowLeft size={14} /> Back to role selection
          </Link>

          <form data-testid="league-signup-form" onSubmit={submit} style={{ display: "grid", gap: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>First name *</label>
                <input data-testid="ls-first-name" className="input-field" value={form.first_name} onChange={upd("first_name")} required style={{ marginTop: 4 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Last name *</label>
                <input data-testid="ls-last-name" className="input-field" value={form.last_name} onChange={upd("last_name")} required style={{ marginTop: 4 }} />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Phone number *</label>
              <input data-testid="ls-phone" type="tel" className="input-field" value={form.phone} onChange={upd("phone")} required style={{ marginTop: 4 }} />
            </div>

            <div>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Email *</label>
              <input data-testid="ls-email" type="email" className="input-field" value={form.email} onChange={upd("email")} required style={{ marginTop: 4 }} />
            </div>

            <div>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Area you play *</label>
              <select
                data-testid="ls-area"
                className="input-field"
                value={form.area}
                onChange={upd("area")}
                required
                style={{ marginTop: 4 }}
              >
                <option value="">-- Select area --</option>
                {AREAS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "block", marginBottom: 8 }}>I'm signing up a *</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <button
                  type="button"
                  data-testid="ls-type-league"
                  onClick={() => setSignupType("league")}
                  style={pickerStyle(form.signup_type === "league")}
                >
                  <Trophy size={18} style={{ color: "var(--cyan)" }} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontWeight: 700, color: "#fff", fontSize: 14 }}>League</div>
                    <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>Multiple organizations</div>
                  </div>
                  {form.signup_type === "league" && <CheckCircle2 size={16} style={{ color: "var(--cyan)", marginLeft: "auto" }} />}
                </button>
                <button
                  type="button"
                  data-testid="ls-type-org"
                  onClick={() => setSignupType("org")}
                  style={pickerStyle(form.signup_type === "org")}
                >
                  <Building2 size={18} style={{ color: "var(--cyan)" }} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontWeight: 700, color: "#fff", fontSize: 14 }}>Organization</div>
                    <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>Multiple teams</div>
                  </div>
                  {form.signup_type === "org" && <CheckCircle2 size={16} style={{ color: "var(--cyan)", marginLeft: "auto" }} />}
                </button>
              </div>
            </div>

            {form.signup_type === "league" && (
              <div data-testid="ls-league-section" style={{ display: "grid", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>How many organizations? *</label>
                  <input
                    data-testid="ls-org-count"
                    type="number"
                    min={1}
                    max={50}
                    className="input-field"
                    value={form.org_count}
                    onChange={(e) => setCount("org", e.target.value)}
                    style={{ marginTop: 4, maxWidth: 140 }}
                  />
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>Organization names *</div>
                  {form.org_names.map((name, i) => (
                    <input
                      key={i}
                      data-testid={`ls-org-name-${i}`}
                      className="input-field"
                      placeholder={`Organization #${i + 1}`}
                      value={name}
                      onChange={(e) => setNameAt("org", i, e.target.value)}
                    />
                  ))}
                </div>
              </div>
            )}

            {form.signup_type === "org" && (
              <div data-testid="ls-org-section" style={{ display: "grid", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>How many teams? *</label>
                  <input
                    data-testid="ls-team-count"
                    type="number"
                    min={1}
                    max={50}
                    className="input-field"
                    value={form.team_count}
                    onChange={(e) => setCount("team", e.target.value)}
                    style={{ marginTop: 4, maxWidth: 140 }}
                  />
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>Team names *</div>
                  {form.team_names.map((name, i) => (
                    <input
                      key={i}
                      data-testid={`ls-team-name-${i}`}
                      className="input-field"
                      placeholder={`Team #${i + 1}`}
                      value={name}
                      onChange={(e) => setNameAt("team", i, e.target.value)}
                    />
                  ))}
                </div>
              </div>
            )}

            {error && <p data-testid="ls-error" style={{ color: "#f44336", fontSize: 13, margin: 0 }}>{error}</p>}

            <button
              type="submit"
              data-testid="ls-submit"
              disabled={submitting}
              className="btn-primary"
              style={{ marginTop: 6, padding: "14px 20px", fontSize: 15, fontWeight: 700, opacity: submitting ? 0.6 : 1 }}
            >
              {submitting ? "Submitting…" : "Submit signup request"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const pickerStyle = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  borderRadius: 12,
  background: active ? "rgba(0,229,255,0.08)" : "var(--dark-card)",
  border: `1px solid ${active ? "var(--cyan)" : "var(--dark-border)"}`,
  cursor: "pointer",
  transition: "all 0.15s ease",
  fontFamily: "'DM Sans', sans-serif",
});

export default LeagueOrgSignupPage;
